// components/workspace-timeline/useWorkspaceTimelinePlaybackController.ts
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { normalizeVideoDimension, toFinite, type TimelineCompositionStatus } from '#lib/timeline-video-types';
import type { KernelHandle } from '#types/types';
import { clamp, getNormalizedAudioGain } from './presentation';
import { waitForTimelineExportReadyViaSocket } from './useTimelineExportRealtimeStatus';
import type {
  ResolvedWorkspaceTimelineTrack,
  WorkspaceComposedPlaybackState,
  WorkspacePlaybackMediaClockHint,
  WorkspaceTimelineTrack,
} from './types';
import { useKernelStore } from '@/kernel/kernelStores';
import {
  getWorkspaceIsTimelineMuted,
  getWorkspaceIsTimelinePlaying,
  getWorkspaceTimelinePlaybackRate,
  selectWorkspaceIsTimelineLoopEnabled,
  selectWorkspaceIsTimelineMuted,
  selectWorkspaceIsTimelinePlaying,
  selectWorkspaceTimelinePlaybackRate,
  setWorkspaceIsTimelineLoopEnabled,
  setWorkspaceIsTimelineMuted,
  setWorkspaceIsTimelinePlaying,
  setWorkspaceTimelinePlaybackRate,
} from '#lib/workspace-runtime-playback-state';

const CURSOR_COMMIT_INTERVAL_MS = 50;
// Workspace playback stays browser-native (fast path).
// High-quality composition remains in the export pipeline.
const COMPOSED_WORKSPACE_PLAYBACK_ENABLED = false;
// TEMP (option A trial): kill-switch to bypass the canvas compositor and fall back to
// the main-display path (syncTimelineVideoElementsAtCursor drives the real layer
// <video> elements to the cursor). Set to false to evaluate whether direct layer
// rendering is enough on its own. Flip back to true to restore the compositor.
const TIMELINE_CANVAS_COMPOSITOR_ENABLED = false;
const COMPOSED_PREPARE_TIMEOUT_MS = 240000;
const DEFAULT_TIMELINE_PLAYBACK_FPS = 30;
const MAX_END_HOLD_FRAME_MS = 100;
const DEFAULT_TIMELINE_PLAYBACK_RATE = 1;
const MIN_TIMELINE_PLAYBACK_RATE = 0.25;
const MAX_TIMELINE_PLAYBACK_RATE = 4;
const TIMELINE_PLAYBACK_RATE_EPSILON = 0.001;
const TIMELINE_END_RESTART_MARGIN_MS = 200;
const VIDEO_FORWARD_SYNC_THRESHOLD_SEC = 0.28;
const AUDIO_FORWARD_SYNC_THRESHOLD_SEC = 0.24;

const formatTimelinePlaybackRate = (rate: number): string => {
  const rounded = Math.round(rate * 100) / 100;
  if (Math.abs(rounded - Math.round(rounded)) < TIMELINE_PLAYBACK_RATE_EPSILON) {
    return `x${Math.round(rounded)}`;
  }
  return `x${rounded.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}`;
};

const normalizeMediaSourceForCompare = (source: string): string => {
  const trimmed = source.trim();
  if (!trimmed) return '';
  try {
    if (typeof window !== 'undefined') {
      return new URL(trimmed, window.location.origin).href;
    }
  } catch {
    // Fallback to raw source when URL normalization fails.
  }
  return trimmed;
};

const getTrackSource = (track: Pick<WorkspaceTimelineTrack, 'layerType' | 'audioSrc' | 'videoSrc'>): string => {
  if (track.layerType === 'audio') {
    return typeof track.audioSrc === 'string' ? track.audioSrc.trim() : '';
  }
  return typeof track.videoSrc === 'string' ? track.videoSrc.trim() : '';
};

const normalizeTrackEffectRatio = (value: unknown): number => {
  const parsed = toFinite(value);
  if (parsed === null) return 0;
  return clamp(parsed, 0, 1);
};

const computeTrackPlaybackOpacityAtMs = (
  track: { startMs: number; endMs: number; activeDurationMs: number; fadeInRatio: number; fadeOutRatio: number; opacity?: number },
  timelineMs: number
): number => {
  const durationMs = Math.max(1, track.activeDurationMs);
  const clampedTimelineMs = clamp(timelineMs, track.startMs, track.endMs);
  const localMs = clamp(clampedTimelineMs - track.startMs, 0, durationMs);
  const fadeInDurationMs = durationMs * clamp(track.fadeInRatio, 0, 1);
  const fadeOutDurationMs = durationMs * clamp(track.fadeOutRatio, 0, 1);
  let opacity = typeof track.opacity === 'number' && Number.isFinite(track.opacity)
    ? clamp(track.opacity, 0, 1)
    : 1;
  if (fadeInDurationMs > 0.001) {
    opacity *= clamp(localMs / fadeInDurationMs, 0, 1);
  }
  if (fadeOutDurationMs > 0.001) {
    const fadeOutStartLocalMs = durationMs - fadeOutDurationMs;
    const fadeOutProgress = clamp((localMs - fadeOutStartLocalMs) / fadeOutDurationMs, 0, 1);
    opacity *= clamp(1 - fadeOutProgress, 0, 1);
  }
  return clamp(opacity, 0, 1);
};

const resolveEndHoldCursorMs = (
  playbackStartMs: number,
  playbackEndMs: number,
  frameDurationMs: number
): number => {
  const startMs = Math.max(0, playbackStartMs);
  const endMs = Math.max(startMs, playbackEndMs);
  if (endMs <= startMs) return startMs;
  const safeFrameDurationMs = clamp(
    Number.isFinite(frameDurationMs) ? frameDurationMs : 1000 / DEFAULT_TIMELINE_PLAYBACK_FPS,
    1,
    Math.min(MAX_END_HOLD_FRAME_MS, Math.max(1, endMs - startMs))
  );
  return Math.max(startMs, endMs - safeFrameDurationMs);
};

export interface UseWorkspaceTimelinePlaybackControllerArgs {
  kernel: React.RefObject<KernelHandle | null>;
  workspaceId: string;
  isActive: boolean;
  showVersionView: boolean;
  clampedCursorMs: number;
  durationMs: number;
  playbackStartMs: number;
  playbackEndMs: number;
  hasAnyTrack: boolean;
  hasPlayableMediaTrack: boolean;
  resolvedTracks: ResolvedWorkspaceTimelineTrack[];
  visibleResolvedTracks: ResolvedWorkspaceTimelineTrack[];
  previewResolvedTracks: WorkspaceTimelineTrack[];
  workspaceFrameW: number | null;
  workspaceFrameH: number | null;
  workspaceTimelineFps: unknown;
  rulerRangeStartMs: number;
  rulerRangeEndMs: number;
  rulerRangeMs: number;
  onCursorChange: (nextCursorMs: number) => void;
  onToggleVersionView: () => void;
  composedPlaybackVideoRef?: React.RefObject<HTMLVideoElement | null>;
  playbackMediaClockHintRef?: React.MutableRefObject<WorkspacePlaybackMediaClockHint>;
  onComposedPlaybackStateChange?: (state: WorkspaceComposedPlaybackState) => void;
}

export interface UseWorkspaceTimelinePlaybackControllerResult {
  isPlaying: boolean;
  isLoopEnabled: boolean;
  isMuted: boolean;
  timelinePlaybackRate: number;
  isAtTimelineEnd: boolean;
  isComposedPreparing: boolean;
  primaryControlLabel: string;
  playbackRateLabel: string;
  clampedCursorMsRef: React.MutableRefObject<number>;
  rulerRef: React.MutableRefObject<HTMLDivElement | null>;
  handlePrimaryAction: () => void;
  handleStepBackward: () => void;
  handleStepForward: () => void;
  handleToggleLoop: () => void;
  handleToggleMuted: () => void;
  handleSelectPlaybackRate: (rate: number) => void;
  handleToggleVersionView: () => void;
  handleRulerMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void;
  seekPlaybackCursor: (nextCursorMs: number) => void;
  pausePlaybackForEdit: () => void;
}

export const useWorkspaceTimelinePlaybackController = ({
  kernel,
  workspaceId,
  isActive,
  showVersionView,
  clampedCursorMs,
  durationMs,
  playbackStartMs,
  playbackEndMs,
  hasAnyTrack,
  hasPlayableMediaTrack,
  resolvedTracks,
  visibleResolvedTracks,
  previewResolvedTracks,
  workspaceFrameW,
  workspaceFrameH,
  workspaceTimelineFps,
  rulerRangeStartMs,
  rulerRangeEndMs,
  rulerRangeMs,
  onCursorChange,
  onToggleVersionView,
  composedPlaybackVideoRef,
  playbackMediaClockHintRef,
  onComposedPlaybackStateChange,
}: UseWorkspaceTimelinePlaybackControllerArgs): UseWorkspaceTimelinePlaybackControllerResult => {
  // Playing / loop / mute / rate live in the shared StudioWorkspace runtime overlay so
  // every view of the workspace observes one source of truth. Reactive reads via
  // selectors; writes via the kernel setters below.
  const isPlaying = useKernelStore((state) => selectWorkspaceIsTimelinePlaying(state, workspaceId));
  const isLoopEnabled = useKernelStore((state) => selectWorkspaceIsTimelineLoopEnabled(state, workspaceId));
  const isMuted = useKernelStore((state) => selectWorkspaceIsTimelineMuted(state, workspaceId));
  const timelinePlaybackRate = useKernelStore((state) => selectWorkspaceTimelinePlaybackRate(state, workspaceId));
  const setIsPlaying = useCallback((next: boolean) => {
    const k = kernel.current;
    if (!k) return;
    setWorkspaceIsTimelinePlaying(k, workspaceId, next);
  }, [kernel, workspaceId]);
  const audioElementsRef = useRef<Record<string, HTMLAudioElement>>({});
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioGainRoutingRef = useRef<Map<string, {
    element: HTMLAudioElement;
    sourceNode: MediaElementAudioSourceNode;
    gainNode: GainNode;
  }>>(new Map());
  const previouslyActiveAudioLayerIdsRef = useRef<Set<string>>(new Set());
  const cursorRef = useRef<number>(0);
  const forceVideoSyncRef = useRef<boolean>(false);
  const forceAudioSyncRef = useRef<boolean>(false);
  const previousActiveVideoLayerIdRef = useRef<string | null>(null);
  // Last currentTime we explicitly commanded per video layer while paused. Paused seeks are
  // decided against THIS (not videoElement.currentTime) because a seek snaps to the nearest
  // decoded frame — so drift-from-currentTime can't tell a real 1-frame step from snap error,
  // which forced the old ~50ms margin that swallowed single-frame steps at 30fps.
  const lastCommandedVideoTimeSecRef = useRef<Record<string, number>>({});
  const mediaClockHoldUntilPerfMsRef = useRef<number>(0);
  const suppressVideoPrewarmUntilPerfMsRef = useRef<number>(0);
  const lastCursorCommitTsRef = useRef<number>(0);
  const playbackClockStartPerfMsRef = useRef<number>(0);
  const playbackClockStartCursorMsRef = useRef<number>(0);
  const clampedCursorMsRef = useRef<number>(0);
  const rulerRef = useRef<HTMLDivElement | null>(null);
  const rulerDragListenersRef = useRef<{ move: (event: MouseEvent) => void; up: (event: MouseEvent) => void } | null>(null);
  const composedPlaybackSignatureRef = useRef<string>('');
  const composedPlaybackAbortRef = useRef<AbortController | null>(null);
  const composedPlayRetryTimerRef = useRef<number | null>(null);
  const pausedComposedTimeSecRef = useRef<number | null>(null);
  const pendingSeekCursorMsRef = useRef<number | null>(null);
  const pendingPlayAfterComposeReadyRef = useRef<boolean>(false);
  const fastPlaybackPreparedSignatureRef = useRef<string>('');
  const [composedPlaybackSourceUrl, setComposedPlaybackSourceUrl] = useState<string | null>(null);
  const [isPreparingComposedPlayback, setIsPreparingComposedPlayback] = useState(false);
  const [composedPreparePercent, setComposedPreparePercent] = useState(0);
  const [composedPrepareMessage, setComposedPrepareMessage] = useState<string | null>(null);
  const [composedMediaSyncTick, setComposedMediaSyncTick] = useState(0);

  useEffect(() => {
    clampedCursorMsRef.current = clampedCursorMs;
  }, [clampedCursorMs]);

  const getAudioContext = useCallback((): AudioContext | null => {
    if (typeof window === 'undefined') return null;
    if (audioContextRef.current) return audioContextRef.current;
    const AudioContextCtor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) return null;
    audioContextRef.current = new AudioContextCtor();
    return audioContextRef.current;
  }, []);

  const ensureAudioGainNode = useCallback((layerId: string, audioElement: HTMLAudioElement): GainNode | null => {
    const context = getAudioContext();
    if (!context) return null;
    const existing = audioGainRoutingRef.current.get(layerId);
    if (existing && existing.element === audioElement) {
      return existing.gainNode;
    }
    if (existing) {
      try { existing.sourceNode.disconnect(); } catch {}
      try { existing.gainNode.disconnect(); } catch {}
      audioGainRoutingRef.current.delete(layerId);
    }
    try {
      const sourceNode = context.createMediaElementSource(audioElement);
      const gainNode = context.createGain();
      sourceNode.connect(gainNode);
      gainNode.connect(context.destination);
      audioGainRoutingRef.current.set(layerId, { element: audioElement, sourceNode, gainNode });
      return gainNode;
    } catch (error) {
      console.warn('[Timeline] Failed to route audio through gain node.', { layerId, error });
      return null;
    }
  }, [getAudioContext]);

  const setTimelineAudioOutputGain = useCallback((
    layerId: string,
    audioElement: HTMLAudioElement,
    muted: boolean,
    gain: number
  ) => {
    const effectiveGain = getNormalizedAudioGain(gain);
    const gainNode = ensureAudioGainNode(layerId, audioElement);
    if (gainNode) {
      audioElement.muted = false;
      audioElement.volume = 1;
      gainNode.gain.value = muted ? 0 : effectiveGain;
      return;
    }
    audioElement.muted = muted;
    audioElement.volume = muted ? 0 : Math.min(1, effectiveGain);
  }, [ensureAudioGainNode]);

  useEffect(() => {
    if (!isPlaying) return;
    const context = getAudioContext();
    if (!context || context.state !== 'suspended') return;
    void context.resume().catch(() => {});
  }, [getAudioContext, isPlaying]);

  const canPlay = isPlaying && isActive && !showVersionView;
  const fastPlaybackRequestSignature = useMemo(
    () =>
      JSON.stringify(
        visibleResolvedTracks
          .filter((track) => track.layerType === 'video' || track.layerType === 'audio')
          .map((track) => ({
            layerId: track.layerId,
            layerType: track.layerType,
            source: getTrackSource(track),
            startMs: Math.round(track.startMs),
            endMs: Math.round(track.endMs),
            sourceInMs: Math.round(track.sourceInMs),
          }))
      ),
    [visibleResolvedTracks]
  );
  const timelinePlaybackFps = useMemo(() => {
    const parsedFps = toFinite(workspaceTimelineFps);
    if (parsedFps === null) return DEFAULT_TIMELINE_PLAYBACK_FPS;
    return clamp(parsedFps, 1, 120);
  }, [workspaceTimelineFps]);
  const endHoldFrameDurationMs = useMemo(
    () => 1000 / timelinePlaybackFps,
    [timelinePlaybackFps]
  );
  const playbackEndHoldCursorMs = useMemo(
    () => resolveEndHoldCursorMs(playbackStartMs, playbackEndMs, endHoldFrameDurationMs),
    [endHoldFrameDurationMs, playbackEndMs, playbackStartMs]
  );
  const isAtTimelineEnd = clampedCursorMs >= Math.max(playbackStartMs, playbackEndHoldCursorMs - 1);
  const requiresComposedPlayback =
    COMPOSED_WORKSPACE_PLAYBACK_ENABLED &&
    hasPlayableMediaTrack &&
    !showVersionView;
  const associatedVideoLayerIdsFromVisibleAudio = useMemo(() => {
    const associatedLayerIds = new Set<string>();
    visibleResolvedTracks.forEach((track) => {
      if (track.layerType !== 'audio') return;
      const parentLayerId =
        typeof track.separatedFromLayerId === 'string'
          ? track.separatedFromLayerId.trim()
          : '';
      if (!parentLayerId) return;
      associatedLayerIds.add(parentLayerId);
    });
    return associatedLayerIds;
  }, [visibleResolvedTracks]);
  const isVideoTimelineAudioMuted = useCallback((track: WorkspaceTimelineTrack): boolean => {
    if (track.layerType !== 'video') return false;
    if (track.mutedInTimeline) return true;
    return associatedVideoLayerIdsFromVisibleAudio.has(track.layerId);
  }, [associatedVideoLayerIdsFromVisibleAudio]);

  const buildComposedPlaybackRequest = useCallback(() => {
    const requestedWidth = normalizeVideoDimension(toFinite(workspaceFrameW) ?? 1280);
    const requestedHeight = normalizeVideoDimension(toFinite(workspaceFrameH) ?? 720);
    const outputFps = Math.max(8, Math.min(30, Math.round(toFinite(workspaceTimelineFps) ?? 24)));
    const normalizedTracks = visibleResolvedTracks
      .map((track) => {
        const sourceSrc = track.layerType === 'image'
          ? (typeof track.imageSrc === 'string' ? track.imageSrc.trim() : '')
          : (track.layerType === 'audio'
            ? (typeof track.audioSrc === 'string' ? track.audioSrc.trim() : '')
            : (typeof track.videoSrc === 'string' ? track.videoSrc.trim() : ''));
        if (!sourceSrc) return null;
        return {
          layerId: track.layerId,
          layerType: track.layerType,
          sourceSrc,
          videoSrc: track.layerType === 'video' || track.layerType === 'audio' ? sourceSrc : null,
          imageSrc: track.layerType === 'image' ? sourceSrc : null,
          audioEnabled:
            track.layerType === 'image'
              ? false
              : (track.layerType === 'audio'
                ? true
                : !isVideoTimelineAudioMuted(track)),
          audioGain:
            track.layerType === 'audio'
              ? getNormalizedAudioGain(track.audioGain)
              : 1,
          renderOrder: track.renderOrder,
          startMs: Math.max(0, Math.round(track.startMs)),
          endMs: Math.max(Math.round(track.startMs + 1), Math.round(track.endMs)),
          sourceInMs: track.layerType === 'image' ? 0 : Math.max(0, Math.round(track.sourceInMs)),
          playbackRate: track.playbackRate,
          fadeInRatio: track.layerType === 'audio' ? 0 : normalizeTrackEffectRatio(track.fadeInRatio),
          fadeOutRatio: track.layerType === 'audio' ? 0 : normalizeTrackEffectRatio(track.fadeOutRatio),
          rotationDeg: track.layerType === 'audio' ? 0 : (track.rotationDeg ?? 0),
          flipX: track.layerType !== 'audio' && track.flipX === true,
          flipY: track.layerType !== 'audio' && track.flipY === true,
          opacity: track.layerType === 'audio' ? 1 : (typeof track.opacity === 'number' && Number.isFinite(track.opacity) ? track.opacity : 1),
          rect: {
            x: Number.isFinite(track.rect?.x) ? Number(track.rect.x) : 0,
            y: Number.isFinite(track.rect?.y) ? Number(track.rect.y) : 0,
            w: Number.isFinite(track.rect?.w) ? Number(track.rect.w) : 0,
            h: Number.isFinite(track.rect?.h) ? Number(track.rect.h) : 0,
          },
          scale: typeof track.scale === 'number' && Number.isFinite(track.scale) ? track.scale : 1,
        };
      })
      .filter((track): track is NonNullable<typeof track> => Boolean(track));

    return {
      workspaceId,
      output: {
        width: requestedWidth,
        height: requestedHeight,
        fps: outputFps,
        requireMp4: true,
        includeAudio: true,
        profile: 'workspace-preview' as const,
      },
      range: {
        startMs: Math.max(0, Math.round(playbackStartMs)),
        endMs: Math.max(Math.round(playbackStartMs + 1), Math.round(playbackEndMs)),
      },
      tracks: normalizedTracks,
    };
  }, [isVideoTimelineAudioMuted, playbackEndMs, playbackStartMs, visibleResolvedTracks, workspaceFrameH, workspaceFrameW, workspaceId, workspaceTimelineFps]);

  const composedRequestSignature = useMemo(
    () => JSON.stringify(buildComposedPlaybackRequest()),
    [buildComposedPlaybackRequest]
  );
  const composedPlaybackEnabled =
    requiresComposedPlayback &&
    typeof composedPlaybackSourceUrl === 'string' &&
    composedPlaybackSourceUrl.length > 0;
  const composedPlaybackRequired = requiresComposedPlayback;
  const timelineCanvasCompositorEnabled =
    TIMELINE_CANVAS_COMPOSITOR_ENABLED &&
    isActive &&
    !showVersionView &&
    hasPlayableMediaTrack;
  const isComposedPreparing = isPreparingComposedPlayback;
  const isFastPlaybackPrepared =
    hasPlayableMediaTrack &&
    fastPlaybackPreparedSignatureRef.current.length > 0 &&
    fastPlaybackPreparedSignatureRef.current === fastPlaybackRequestSignature;

  const resetPlaybackClocksForDiscontinuity = useCallback((cursorMs: number) => {
    const targetCursorMs = Number.isFinite(cursorMs)
      ? clamp(cursorMs, 0, durationMs)
      : 0;
    const nowMs = typeof performance !== 'undefined' ? performance.now() : Date.now();
    mediaClockHoldUntilPerfMsRef.current = Math.max(mediaClockHoldUntilPerfMsRef.current, nowMs + 420);
    suppressVideoPrewarmUntilPerfMsRef.current = Math.max(suppressVideoPrewarmUntilPerfMsRef.current, nowMs + 420);
    if (playbackMediaClockHintRef) {
      playbackMediaClockHintRef.current = {
        cursorMs: null,
        isWaitingForMedia: false,
        updatedAtMs: nowMs,
      };
    }
    // Only hard-reset the canvas compositor for playback discontinuities. When paused
    // (frame stepping, ruler scrubbing), tearing it down commits a frame with the
    // compositor unmounted — flashing the raw layer canvas / a re-initialising frame
    // before the publish effect re-enables it. Paused, the compositor re-seeks to the
    // new cursor on its own and holds the previous frame meanwhile, so leave it mounted.
    // Read playing straight from the shared workspace runtime (kernel is a stable ref)
    // so this callback stays stable instead of subscribing to the flag.
    const k = kernel.current;
    const isPlayingNow = k ? getWorkspaceIsTimelinePlaying(k, workspaceId) : false;
    if (timelineCanvasCompositorEnabled && isPlayingNow) {
      onComposedPlaybackStateChange?.({
        enabled: false,
        sourceUrl: null,
        isPlaying: false,
        isMuted,
        isPreparing: false,
        preparePercent: 0,
        prepareMessage: null,
        tracks: [],
        cursorMs: targetCursorMs,
        playbackRate: timelinePlaybackRate,
        playbackStartMs,
        playbackEndMs,
      });
    }
  }, [
    durationMs,
    isMuted,
    kernel,
    onComposedPlaybackStateChange,
    playbackEndMs,
    playbackMediaClockHintRef,
    playbackStartMs,
    timelineCanvasCompositorEnabled,
    timelinePlaybackRate,
    workspaceId,
  ]);

  const prepareFastPlaybackReady = useCallback(async (): Promise<void> => {
    if (!hasPlayableMediaTrack) return;
    const signature = fastPlaybackRequestSignature;
    if (!signature) return;
    if (fastPlaybackPreparedSignatureRef.current === signature) {
      return;
    }

    // OBSOLETE pre-buffering step — intentionally a no-op fast path.
    // The kernel now downloads every video's bytes once into `layer.videoBlob` during
    // hydration, and playback runs on the kernel/wall-clock master (media chases the cursor,
    // never the reverse). There is nothing to pre-buffer here and nothing to block play on,
    // so mark this signature prepared immediately: no "Buffering timeline media…" spinner and
    // no play-start stall. (Kept as a stable readiness gate so isFastPlaybackPrepared flips
    // true; the actual buffering is the kernel's blob prefetch.)
    fastPlaybackPreparedSignatureRef.current = signature;
    setComposedPreparePercent(100);
    setComposedPrepareMessage('Ready');
    setIsPreparingComposedPlayback(false);
  }, [
    fastPlaybackRequestSignature,
    hasPlayableMediaTrack,
  ]);

  const ensureComposedPlaybackReady = useCallback(async (): Promise<boolean> => {
    if (!hasPlayableMediaTrack) return false;
    const requestPayload = buildComposedPlaybackRequest();
    if (!Array.isArray(requestPayload.tracks) || requestPayload.tracks.length === 0) {
      kernel.current?.handleError('No valid timeline tracks were available for composed playback.');
      return false;
    }
    const signature = composedRequestSignature;
    if (
      composedPlaybackSignatureRef.current === signature &&
      typeof composedPlaybackSourceUrl === 'string' &&
      composedPlaybackSourceUrl.length > 0
    ) {
      return true;
    }

    composedPlaybackAbortRef.current?.abort();
    const abortController = new AbortController();
    composedPlaybackAbortRef.current = abortController;
    setIsPreparingComposedPlayback(true);
    setComposedPreparePercent(1);
    setComposedPrepareMessage('Analyzing timeline...');

    type ComposeStatus = {
      status?: 'processing' | 'ready' | 'error' | 'missing';
      phase?: 'queued' | 'pulling' | 'building' | 'finalizing' | 'ready' | 'error' | 'missing';
      percent?: number;
      message?: string;
      error?: string;
    };
    type ComposeEnvelope = { cacheKey?: string; status?: ComposeStatus; realtime?: { enabled?: boolean } };
    const applyStatus = (statusPayload?: ComposeStatus) => {
      if (!statusPayload) return;
      const percent = Number.isFinite(statusPayload.percent) ? Number(statusPayload.percent) : 0;
      setComposedPreparePercent(clamp(Math.round(percent), 0, 100));
      const nextMessage =
        typeof statusPayload.message === 'string' && statusPayload.message.trim().length > 0
          ? statusPayload.message.trim()
          : null;
      setComposedPrepareMessage(nextMessage);
    };

    try {
      const prepareResponse = await fetch('/api/export/timeline-video?mode=prepare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestPayload),
        signal: abortController.signal,
      });
      if (!prepareResponse.ok) {
        const errorText = await prepareResponse.text().catch(() => '');
        throw new Error(errorText || `Composed playback prepare failed (${prepareResponse.status}).`);
      }
      const preparePayload = (await prepareResponse.json()) as ComposeEnvelope;
      const cacheKey = typeof preparePayload.cacheKey === 'string' ? preparePayload.cacheKey.trim() : '';
      if (!cacheKey) {
        throw new Error('Composed playback did not return a cache key.');
      }

      const statusPayload = await waitForTimelineExportReadyViaSocket({
        kernel,
        cacheKey,
        initialStatus: preparePayload.status as TimelineCompositionStatus | undefined,
        timeoutMs: COMPOSED_PREPARE_TIMEOUT_MS,
        abortSignal: abortController.signal,
        onStatus: applyStatus,
        socketEnabled: preparePayload.realtime?.enabled !== false,
      });

      if (statusPayload.status !== 'ready') {
        throw new Error('Composed playback did not reach ready state.');
      }

      composedPlaybackSignatureRef.current = signature;
      forceVideoSyncRef.current = true;
      forceAudioSyncRef.current = true;
      setComposedPlaybackSourceUrl(
        `/api/export/timeline-video?mode=file&cacheKey=${encodeURIComponent(cacheKey)}&v=${Date.now()}`
      );
      setComposedPreparePercent(100);
      setComposedPrepareMessage('Ready');
      return true;
    } catch (error: any) {
      if (abortController.signal.aborted) return false;
      setComposedPreparePercent(0);
      setComposedPrepareMessage(null);
      kernel.current?.handleError(
        error?.message || 'Failed to prepare timeline preview playback.'
      );
      return false;
    } finally {
      if (composedPlaybackAbortRef.current === abortController) {
        composedPlaybackAbortRef.current = null;
      }
      setIsPreparingComposedPlayback(false);
    }
  }, [buildComposedPlaybackRequest, composedPlaybackSourceUrl, composedRequestSignature, hasPlayableMediaTrack, kernel]);

  useEffect(() => {
    if (!isActive || showVersionView || !composedPlaybackRequired) return;
    if (isPreparingComposedPlayback) return;
    const hasComposedSourceUrl =
      typeof composedPlaybackSourceUrl === 'string' && composedPlaybackSourceUrl.length > 0;
    const hasCurrentSignatureSource =
      hasComposedSourceUrl && composedPlaybackSignatureRef.current === composedRequestSignature;
    if (hasCurrentSignatureSource) return;
    void ensureComposedPlaybackReady();
  }, [
    composedPlaybackSourceUrl,
    composedPlaybackRequired,
    composedRequestSignature,
    ensureComposedPlaybackReady,
    isActive,
    isPreparingComposedPlayback,
    showVersionView,
  ]);

  useEffect(() => {
    if (!hasPlayableMediaTrack || showVersionView) {
      fastPlaybackPreparedSignatureRef.current = '';
      setIsPreparingComposedPlayback(false);
      setComposedPreparePercent(0);
      setComposedPrepareMessage(null);
      return;
    }
    if (fastPlaybackPreparedSignatureRef.current === fastPlaybackRequestSignature) {
      return;
    }
    if (
      fastPlaybackPreparedSignatureRef.current.length > 0 &&
      fastPlaybackPreparedSignatureRef.current !== fastPlaybackRequestSignature
    ) {
      setComposedPreparePercent(0);
      setComposedPrepareMessage('Queued');
    }
    forceVideoSyncRef.current = true;
    forceAudioSyncRef.current = true;
  }, [
    fastPlaybackRequestSignature,
    hasPlayableMediaTrack,
    showVersionView,
  ]);

  useEffect(() => {
    if (!isActive || showVersionView || !hasPlayableMediaTrack) return;
    if (fastPlaybackPreparedSignatureRef.current === fastPlaybackRequestSignature) return;
    if (isPreparingComposedPlayback) return;
    void prepareFastPlaybackReady();
  }, [
    fastPlaybackRequestSignature,
    hasPlayableMediaTrack,
    isActive,
    isPreparingComposedPlayback,
    prepareFastPlaybackReady,
    showVersionView,
  ]);

  // NOTE: the former offscreen video-preload pool lived here. It created a second <video>
  // per track pointed at the raw asset URL and called load() — a full redundant network
  // download of every clip on top of the layer's own <video>, a prime cause of connection
  // congestion / `stalled` failures. It is obsolete now that the hydrator downloads each
  // video's bytes once into layer.videoBlob and Layer2DVideo plays from a local blob: URL.

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (composedPlaybackRequired) {
      Object.values(audioElementsRef.current).forEach((audioElement) => {
        try {
          audioElement.pause();
          audioElement.removeAttribute('src');
          audioElement.load();
        } catch {
          // best effort cleanup
        }
      });
      audioElementsRef.current = {};
      return;
    }
    const sourceByLayerId = new Map<string, string>();
    visibleResolvedTracks.forEach((track) => {
      if (track.layerType !== 'audio' && track.layerType !== 'video') return;
      if (track.layerType === 'video' && isVideoTimelineAudioMuted(track)) return;
      const source = getTrackSource(track);
      if (source.length > 0) {
        sourceByLayerId.set(track.layerId, source);
      }
    });

    Object.entries(audioElementsRef.current).forEach(([layerId, audioElement]) => {
      if (sourceByLayerId.has(layerId)) return;
      try {
        audioElement.pause();
        audioElement.removeAttribute('src');
        audioElement.load();
      } catch {
        // best effort cleanup
      }
      delete audioElementsRef.current[layerId];
    });

    sourceByLayerId.forEach((source, layerId) => {
      const existing = audioElementsRef.current[layerId];
      const normalizedNextSource = normalizeMediaSourceForCompare(source);
      if (existing) {
        if (normalizeMediaSourceForCompare(existing.currentSrc || existing.src) === normalizedNextSource) return;
        try {
          existing.pause();
          existing.removeAttribute('src');
          existing.load();
        } catch {
          // best effort cleanup
        }
      }
      const audioElement = new Audio();
      audioElement.preload = 'auto';
      audioElement.crossOrigin = 'anonymous';
      audioElement.loop = false;
      audioElement.src = source;
      audioElementsRef.current[layerId] = audioElement;
    });
  }, [composedPlaybackRequired, isVideoTimelineAudioMuted, visibleResolvedTracks]);

  useEffect(() => {
    return () => {
      Object.values(audioElementsRef.current).forEach((audioElement) => {
        try {
          audioElement.pause();
          audioElement.removeAttribute('src');
          audioElement.load();
        } catch {
          // best effort cleanup
        }
      });
      audioElementsRef.current = {};
    };
  }, []);

  useEffect(() => {
    return () => {
      audioGainRoutingRef.current.forEach((routing) => {
        try { routing.sourceNode.disconnect(); } catch {}
        try { routing.gainNode.disconnect(); } catch {}
      });
      audioGainRoutingRef.current.clear();
      const context = audioContextRef.current;
      audioContextRef.current = null;
      if (context) {
        void context.close().catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      composedPlaybackAbortRef.current?.abort();
      composedPlaybackAbortRef.current = null;
      if (composedPlayRetryTimerRef.current !== null) {
        window.clearTimeout(composedPlayRetryTimerRef.current);
        composedPlayRetryTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const composedVideoElement = composedPlaybackVideoRef?.current ?? null;
    if (!composedVideoElement || !composedPlaybackEnabled) return;

    const bumpSyncTick = () => {
      setComposedMediaSyncTick((current) => current + 1);
    };
    composedVideoElement.addEventListener('loadedmetadata', bumpSyncTick);
    composedVideoElement.addEventListener('canplay', bumpSyncTick);
    composedVideoElement.addEventListener('seeked', bumpSyncTick);
    composedVideoElement.addEventListener('ended', bumpSyncTick);

    return () => {
      composedVideoElement.removeEventListener('loadedmetadata', bumpSyncTick);
      composedVideoElement.removeEventListener('canplay', bumpSyncTick);
      composedVideoElement.removeEventListener('seeked', bumpSyncTick);
      composedVideoElement.removeEventListener('ended', bumpSyncTick);
    };
  }, [composedPlaybackEnabled, composedPlaybackSourceUrl, composedPlaybackVideoRef]);

  useEffect(() => {
    if (!composedPlaybackRequired) return;
    if (!hasPlayableMediaTrack || showVersionView) {
      composedPlaybackSignatureRef.current = '';
      setComposedPlaybackSourceUrl(null);
      setIsPreparingComposedPlayback(false);
      setComposedPreparePercent(0);
      setComposedPrepareMessage(null);
      return;
    }
    const nextSignature = JSON.stringify(buildComposedPlaybackRequest());
    if (
      composedPlaybackSignatureRef.current &&
      composedPlaybackSignatureRef.current !== nextSignature
    ) {
      setComposedPreparePercent(0);
      setComposedPrepareMessage('Queued');
    }
  }, [buildComposedPlaybackRequest, composedPlaybackRequired, hasPlayableMediaTrack, showVersionView]);

  useEffect(() => {
    if (!isPlaying) {
      cursorRef.current = clampedCursorMs;
      return;
    }
    const liveCursorMs = clamp(cursorRef.current, 0, durationMs);
    // While playing, keep cursor monotonic unless a large external jump happened (seek-like update).
    // Parent cursor updates intentionally trail the local RAF clock; at 2x/4x that lag can exceed
    // small fixed thresholds and pull playback backward.
    const externalSeekThresholdMs = Math.max(750, 1000 * timelinePlaybackRate);
    if (Math.abs(clampedCursorMs - liveCursorMs) > externalSeekThresholdMs) {
      cursorRef.current = clampedCursorMs;
      playbackClockStartPerfMsRef.current = performance.now();
      playbackClockStartCursorMsRef.current = clampedCursorMs;
      forceVideoSyncRef.current = true;
      forceAudioSyncRef.current = true;
    }
  }, [clampedCursorMs, durationMs, isPlaying, timelinePlaybackRate]);

  useEffect(() => {
    if (isPlaying || showVersionView || !hasAnyTrack) return;
    if (playbackEndHoldCursorMs >= playbackEndMs) return;
    if (clampedCursorMs < playbackEndMs) return;

    cursorRef.current = playbackEndHoldCursorMs;
    forceVideoSyncRef.current = true;
    forceAudioSyncRef.current = true;
    playbackClockStartPerfMsRef.current = 0;
    playbackClockStartCursorMsRef.current = playbackEndHoldCursorMs;
    onCursorChange(playbackEndHoldCursorMs);
  }, [
    clampedCursorMs,
    hasAnyTrack,
    isPlaying,
    onCursorChange,
    playbackEndHoldCursorMs,
    playbackEndMs,
    showVersionView,
  ]);

  const syncTimelineVideoElementsAtCursor = useCallback((
    timelineCursorMsRaw: number,
    canPlayNow: boolean,
    nowPerf: number = performance.now()
  ) => {
    if (timelineCanvasCompositorEnabled || composedPlaybackEnabled) return;
    const videoRefs = kernel.current?.videoRefs.current;
    if (!videoRefs) return;
    const timelineCursorMs = clamp(timelineCursorMsRaw, 0, durationMs);
    const isAlteredPlaybackRate =
      Math.abs(timelinePlaybackRate - DEFAULT_TIMELINE_PLAYBACK_RATE) > TIMELINE_PLAYBACK_RATE_EPSILON;
    const liveLayerRegistry = kernel.current?.getLayerRegistry();
    const isLayerCurrentlyVisible = (layerId: string): boolean =>
      liveLayerRegistry?.[layerId]?.visible !== false;
    const activeVisibleVideoTracksAtCursor = visibleResolvedTracks
      .filter((track) => track.layerType === 'video')
      .filter((track) => isLayerCurrentlyVisible(track.layerId))
      .filter((track) => timelineCursorMs >= track.startMs && timelineCursorMs < track.endMs)
      .sort((left, right) => {
        if (left.renderOrder !== right.renderOrder) {
          return left.renderOrder - right.renderOrder;
        }
        return left.layerId.localeCompare(right.layerId);
      });
    // Multi-layer preview: every video track active at the cursor is composited via
    // its own persistent <video> element (native resolution), so overlapping clips
    // crossfade through their per-track opacity instead of the top one hiding the rest.
    // Concurrency is naturally bounded by how many clips overlap at a given instant.
    const activeVisibleVideoLayerIdSet = new Set(
      activeVisibleVideoTracksAtCursor.map((track) => track.layerId)
    );
    const activeAudioSourcesAtCursor = new Set(
      visibleResolvedTracks
        .filter((track) => track.layerType === 'audio')
        .filter((track) => timelineCursorMs >= track.startMs && timelineCursorMs < track.endMs)
        .map((track) => getTrackSource(track))
        .filter((source) => source.length > 0)
        .map((source) => normalizeMediaSourceForCompare(source))
    );
    const activeSeparatedVideoLayerIds = new Set(
      visibleResolvedTracks
        .filter((track) => track.layerType === 'audio')
        .filter((track) => timelineCursorMs >= track.startMs && timelineCursorMs < track.endMs)
        .map((track) => (
          typeof track.separatedFromLayerId === 'string' ? track.separatedFromLayerId.trim() : ''
        ))
        .filter((layerId) => layerId.length > 0)
    );
    // Signature of the full active set (already sorted by renderOrder then layerId):
    // when it changes (a clip enters/leaves the cursor), hold the media clock and
    // hard-sync every active video so overlapping clips stay in step through the cut.
    const activeVisibleVideoLayerSignature = activeVisibleVideoTracksAtCursor
      .map((track) => track.layerId)
      .join('|');
    const didActiveVideoLayerChange =
      previousActiveVideoLayerIdRef.current !== activeVisibleVideoLayerSignature;
    previousActiveVideoLayerIdRef.current = activeVisibleVideoLayerSignature;
    if (canPlayNow && didActiveVideoLayerChange) {
      mediaClockHoldUntilPerfMsRef.current = nowPerf + 360;
    }

    resolvedTracks.forEach((track) => {
      if (track.layerType !== 'video') return;
      const videoElement = videoRefs[track.layerId];
      if (!videoElement) return;
      videoElement.dataset.faterTimelineControlled = 'true';
      videoElement.style.transition = 'none';
      if (!isLayerCurrentlyVisible(track.layerId)) {
        if (!videoElement.paused) {
          videoElement.pause();
        }
        videoElement.muted = true;
        videoElement.volume = 0;
        videoElement.playbackRate = 1;
        videoElement.style.visibility = 'hidden';
        videoElement.style.opacity = '0';
        return;
      }
      videoElement.loop = false;
      const isActiveAtCursor = activeVisibleVideoLayerIdSet.has(track.layerId);
      if (!isActiveAtCursor) {
        // Warm-up: workspaces hold only a handful of short clips, so eagerly prime every
        // not-yet-active video by seeking it to its in-point. preload="auto" then buffers
        // forward from the correct position, making activation (first play and every
        // transition) seamless instead of a cold seek deep into the source. Runs whether
        // or not we are playing so the very first play is already warm. The suppress guard
        // still skips priming during the brief window after a discontinuity/scrub storm.
        if (
          nowPerf >= suppressVideoPrewarmUntilPerfMsRef.current &&
          videoElement.readyState >= 1 &&
          !videoElement.seeking
        ) {
          const prewarmTargetSec = Math.max(0, track.sourceInMs / 1000);
          if (Math.abs(videoElement.currentTime - prewarmTargetSec) > 0.12) {
            try {
              videoElement.currentTime = prewarmTargetSec;
            } catch {
              // best effort prewarm seek
            }
          }
        }
        if (!videoElement.paused) {
          videoElement.pause();
        }
        videoElement.muted = true;
        videoElement.volume = 0;
        videoElement.playbackRate = 1;
        videoElement.style.visibility = 'hidden';
        videoElement.style.opacity = '0';
        return;
      }
      videoElement.style.visibility = 'visible';
      videoElement.style.opacity = `${computeTrackPlaybackOpacityAtMs(track, timelineCursorMs)}`;
      const source = getTrackSource(track);
      const shouldMuteVideoForSeparatedAudio =
        associatedVideoLayerIdsFromVisibleAudio.has(track.layerId) ||
        activeSeparatedVideoLayerIds.has(track.layerId) ||
        (source.length > 0 && activeAudioSourcesAtCursor.has(normalizeMediaSourceForCompare(source)));
      const effectiveMuted = isMuted || !canPlayNow || isVideoTimelineAudioMuted(track) || shouldMuteVideoForSeparatedAudio;
      videoElement.muted = effectiveMuted;
      videoElement.volume = effectiveMuted ? 0 : 1;
      videoElement.playbackRate = timelinePlaybackRate;

      const trackRelativeMs = clamp(
        timelineCursorMs - track.startMs + track.sourceInMs,
        0,
        Number.MAX_SAFE_INTEGER
      );
      const targetTimeSec = trackRelativeMs / 1000;
      const driftSec = Math.abs(videoElement.currentTime - targetTimeSec);
      const forwardDriftSec = targetTimeSec - videoElement.currentTime;
      const forwardSyncThresholdSec = VIDEO_FORWARD_SYNC_THRESHOLD_SEC * Math.max(1, timelinePlaybackRate);
      // Paused: seek whenever the cursor lands on a new frame. Compare the target against the
      // last commanded time (loop-safe vs seek snapping) with a half-frame epsilon, so a single
      // +/-1 frame step always re-seeks while a re-run at the same cursor does not.
      const pausedFrameEpsilonSec = Math.max(0.001, (endHoldFrameDurationMs / 1000) * 0.5);
      const lastCommandedSec = lastCommandedVideoTimeSecRef.current[track.layerId];
      const pausedTargetMoved =
        lastCommandedSec === undefined ||
        Math.abs(targetTimeSec - lastCommandedSec) > pausedFrameEpsilonSec;
      const shouldHardSync =
        forceVideoSyncRef.current ||
        didActiveVideoLayerChange ||
        (!canPlayNow && pausedTargetMoved) ||
        (canPlayNow && forwardDriftSec > forwardSyncThresholdSec);
      if (isAlteredPlaybackRate && canPlayNow) {
        if (!videoElement.paused) {
          videoElement.pause();
        }
        if (
          videoElement.readyState >= 1 &&
          !videoElement.seeking &&
          (shouldHardSync || driftSec > 0.025)
        ) {
          try {
            videoElement.currentTime = targetTimeSec;
            lastCommandedVideoTimeSecRef.current[track.layerId] = targetTimeSec;
          } catch {
            // Metadata may not be ready yet; sync on next tick.
          }
          forceVideoSyncRef.current = false;
        } else if (forceVideoSyncRef.current && !videoElement.seeking) {
          forceVideoSyncRef.current = false;
        }
        return;
      }
      if (shouldHardSync) {
        try {
          videoElement.currentTime = targetTimeSec;
          lastCommandedVideoTimeSecRef.current[track.layerId] = targetTimeSec;
        } catch {
          // Metadata may not be ready yet; sync on next tick.
        }
        forceVideoSyncRef.current = false;
      }

      if (canPlayNow) {
        if (videoElement.paused) {
          void videoElement.play().catch(() => undefined);
        }
      } else if (!videoElement.paused) {
        videoElement.pause();
      }
    });
  }, [
    associatedVideoLayerIdsFromVisibleAudio,
    composedPlaybackEnabled,
    durationMs,
    endHoldFrameDurationMs,
    isMuted,
    isVideoTimelineAudioMuted,
    kernel,
    resolvedTracks,
    timelineCanvasCompositorEnabled,
    timelinePlaybackRate,
    visibleResolvedTracks,
  ]);

  useEffect(() => {
    const videoRefs = kernel.current?.videoRefs.current;
    if (!videoRefs) return;
    if (timelineCanvasCompositorEnabled && !isPreparingComposedPlayback) {
      resolvedTracks.forEach((track) => {
        if (track.layerType !== 'video') return;
        const videoElement = videoRefs[track.layerId];
        if (!videoElement) return;
        if (!videoElement.paused) {
          videoElement.pause();
        }
        videoElement.muted = true;
        videoElement.volume = 0;
        videoElement.playbackRate = 1;
        videoElement.style.visibility = 'hidden';
        videoElement.style.opacity = '0';
      });
      return () => {
        resolvedTracks.forEach((track) => {
          if (track.layerType !== 'video') return;
          const videoElement = videoRefs[track.layerId];
          if (!videoElement) return;
          videoElement.style.visibility = '';
          videoElement.style.opacity = '1';
          videoElement.loop = true;
          videoElement.muted = true;
          videoElement.volume = 0;
          videoElement.playbackRate = 1;
        });
      };
    }
    if (composedPlaybackEnabled) {
      resolvedTracks.forEach((track) => {
        if (track.layerType !== 'video') return;
        const videoElement = videoRefs[track.layerId];
        if (!videoElement) return;
        if (!videoElement.paused) {
          videoElement.pause();
        }
        videoElement.muted = true;
        videoElement.volume = 0;
        videoElement.playbackRate = 1;
        videoElement.style.visibility = 'hidden';
        videoElement.style.opacity = '0';
      });
      return () => {
        resolvedTracks.forEach((track) => {
          if (track.layerType !== 'video') return;
          const videoElement = videoRefs[track.layerId];
          if (!videoElement) return;
          videoElement.style.visibility = '';
          videoElement.style.opacity = '1';
          videoElement.loop = true;
          videoElement.muted = true;
          videoElement.volume = 0;
          videoElement.playbackRate = 1;
        });
      };
    }
    const timelineCursorMs = isPlaying ? clamp(cursorRef.current, 0, durationMs) : clampedCursorMs;
    syncTimelineVideoElementsAtCursor(timelineCursorMs, canPlay);

    return () => {
      // Intentionally no-op for direct timeline playback.
      // This effect re-runs at cursor cadence; resetting loop/visibility in cleanup
      // can transiently re-enable per-track looping and cause visual flicker.
    };
  }, [
    canPlay,
    clampedCursorMs,
    composedPlaybackEnabled,
    durationMs,
    isVideoTimelineAudioMuted,
    isMuted,
    isPlaying,
    isPreparingComposedPlayback,
    kernel,
    resolvedTracks,
    syncTimelineVideoElementsAtCursor,
    visibleResolvedTracks,
    associatedVideoLayerIdsFromVisibleAudio,
    timelineCanvasCompositorEnabled,
    timelinePlaybackRate,
  ]);

  useEffect(() => {
    if (composedPlaybackEnabled) {
      Object.entries(audioElementsRef.current).forEach(([layerId, audioElement]) => {
        if (!audioElement.paused) {
          audioElement.pause();
        }
        setTimelineAudioOutputGain(layerId, audioElement, true, 0);
        audioElement.playbackRate = 1;
      });
      previouslyActiveAudioLayerIdsRef.current = new Set();
      return;
    }
    const timelineCursorMs = isPlaying ? clamp(cursorRef.current, 0, durationMs) : clampedCursorMs;
    const liveLayerRegistry = kernel.current?.getLayerRegistry();
    const isLayerCurrentlyVisible = (layerId: string): boolean =>
      liveLayerRegistry?.[layerId]?.visible !== false;
    const audioTrackByLayerId = new Map(
      visibleResolvedTracks
        .filter((track) => isLayerCurrentlyVisible(track.layerId))
        .filter((track) => track.layerType === 'audio' || track.layerType === 'video')
        .filter((track) => !(track.layerType === 'video' && isVideoTimelineAudioMuted(track)))
        .map((track) => [track.layerId, track] as const)
    );
    const activeAudioLayerIds = new Set(
      visibleResolvedTracks
        .filter((track) => {
          if (!isLayerCurrentlyVisible(track.layerId)) return false;
          if (track.layerType !== 'audio' && track.layerType !== 'video') return false;
          if (track.layerType === 'video' && isVideoTimelineAudioMuted(track)) return false;
          return timelineCursorMs >= track.startMs && timelineCursorMs < track.endMs;
        })
        .map((track) => track.layerId)
    );
    const previouslyActiveAudioLayerIds = previouslyActiveAudioLayerIdsRef.current;

    Object.entries(audioElementsRef.current).forEach(([layerId, audioElement]) => {
      const track = audioTrackByLayerId.get(layerId);
      if (!track || !activeAudioLayerIds.has(layerId)) {
        if (!audioElement.paused) {
          audioElement.pause();
        }
        setTimelineAudioOutputGain(layerId, audioElement, true, 0);
        return;
      }
      const effectiveAudioGain = getNormalizedAudioGain(track.audioGain);
      setTimelineAudioOutputGain(layerId, audioElement, isMuted || !canPlay, effectiveAudioGain);
      audioElement.loop = false;
      audioElement.playbackRate = timelinePlaybackRate;
      const trackRelativeMs = clamp(
        timelineCursorMs - track.startMs + track.sourceInMs,
        0,
        Number.MAX_SAFE_INTEGER
      );
      const targetTimeSec = trackRelativeMs / 1000;
      const becameActive = !previouslyActiveAudioLayerIds.has(layerId);
      // During live playback, only catch up forward drift.
      // Backward corrections are disabled to prevent timeline backtracking artifacts.
      const forwardDriftSec = targetTimeSec - audioElement.currentTime;
      const forwardSyncThresholdSec = AUDIO_FORWARD_SYNC_THRESHOLD_SEC * Math.max(1, timelinePlaybackRate);
      const shouldHardSync =
        forceAudioSyncRef.current ||
        becameActive ||
        (!canPlay && Math.abs(audioElement.currentTime - targetTimeSec) > 0.05) ||
        (canPlay && forwardDriftSec > forwardSyncThresholdSec);
      if (shouldHardSync) {
        try {
          audioElement.currentTime = targetTimeSec;
        } catch {
          // Metadata may not be ready yet; keep playback attempt and sync later.
        }
        forceAudioSyncRef.current = false;
      }
      if (canPlay) {
        if (audioElement.paused) {
          void audioElement.play().catch(() => undefined);
        }
      } else {
        if (!audioElement.paused) {
          audioElement.pause();
        }
      }
    });
    previouslyActiveAudioLayerIdsRef.current = activeAudioLayerIds;
  }, [
    canPlay,
    clampedCursorMs,
    composedPlaybackEnabled,
    durationMs,
    isMuted,
    isPlaying,
    isVideoTimelineAudioMuted,
    visibleResolvedTracks,
    setTimelineAudioOutputGain,
    timelinePlaybackRate,
    kernel,
  ]);

  const hasActiveCanvasVideoAtCursor = useCallback((timelineCursorMs: number): boolean => {
    if (!timelineCanvasCompositorEnabled || composedPlaybackEnabled) return false;
    const clampedTimelineCursorMs = clamp(timelineCursorMs, 0, durationMs);
    const liveLayerRegistry = kernel.current?.getLayerRegistry();
    const isLayerCurrentlyVisible = (layerId: string): boolean =>
      liveLayerRegistry?.[layerId]?.visible !== false;
    return visibleResolvedTracks.some((track) => (
      track.layerType === 'video' &&
      isLayerCurrentlyVisible(track.layerId) &&
      clampedTimelineCursorMs >= track.startMs &&
      clampedTimelineCursorMs < track.endMs
    ));
  }, [
    composedPlaybackEnabled,
    durationMs,
    kernel,
    timelineCanvasCompositorEnabled,
    visibleResolvedTracks,
  ]);

  useEffect(() => {
    if (!canPlay) return;
    let animationFrameId = 0;
    let lastTick = performance.now();
    lastCursorCommitTsRef.current = lastTick;

    const tick = (now: number) => {
      const deltaMs = now - lastTick;
      const playbackDeltaMs = deltaMs * timelinePlaybackRate;
      lastTick = now;
      let nextCursorMs = clamp(cursorRef.current + playbackDeltaMs, 0, durationMs);
      const cursorNowMs = cursorRef.current;
      if (composedPlaybackEnabled) {
        const composedVideoElement = composedPlaybackVideoRef?.current ?? null;
        if (forceVideoSyncRef.current && composedVideoElement?.seeking) {
          nextCursorMs = cursorNowMs;
          cursorRef.current = nextCursorMs;
          animationFrameId = window.requestAnimationFrame(tick);
          return;
        }
        if (forceVideoSyncRef.current) {
          forceVideoSyncRef.current = false;
        }
        if (composedVideoElement) {
          const mediaDurationSec = Number.isFinite(composedVideoElement.duration)
            ? Math.max(0, composedVideoElement.duration)
            : 0;
          const mediaHasEnded =
            composedVideoElement.ended ||
            (mediaDurationSec > 0 && composedVideoElement.currentTime >= Math.max(0, mediaDurationSec - 0.02));
          const canUseMediaClock =
            composedVideoElement.readyState >= 2 &&
            !composedVideoElement.seeking &&
            !composedVideoElement.paused;
          if (canUseMediaClock && !mediaHasEnded) {
            const derivedCursorMs = clamp(
              playbackStartMs + composedVideoElement.currentTime * 1000,
              0,
              durationMs
            );
            if (Number.isFinite(derivedCursorMs)) {
              nextCursorMs = derivedCursorMs;
            } else {
              nextCursorMs = clamp(cursorNowMs + playbackDeltaMs, 0, durationMs);
            }
          } else if (mediaHasEnded) {
            nextCursorMs = playbackEndMs;
          } else {
            // Keep cursor anchored until composed media is actually running.
            nextCursorMs = cursorNowMs;
          }
        } else {
          nextCursorMs = clamp(cursorNowMs + playbackDeltaMs, 0, durationMs);
        }
        cursorRef.current = nextCursorMs;
        const shouldHoldLastFrame = !isLoopEnabled && nextCursorMs >= playbackEndMs;
        const cursorToCommit = shouldHoldLastFrame ? playbackEndHoldCursorMs : nextCursorMs;
        const shouldCommit =
          now - lastCursorCommitTsRef.current >= CURSOR_COMMIT_INTERVAL_MS ||
          cursorToCommit <= 0 ||
          nextCursorMs >= playbackEndMs;
        if (shouldCommit) {
          lastCursorCommitTsRef.current = now;
          onCursorChange(cursorToCommit);
        }
        if (nextCursorMs >= playbackEndMs) {
          if (isLoopEnabled) {
            cursorRef.current = playbackStartMs;
            forceVideoSyncRef.current = true;
            forceAudioSyncRef.current = true;
            resetPlaybackClocksForDiscontinuity(playbackStartMs);
            mediaClockHoldUntilPerfMsRef.current = now + 420;
            suppressVideoPrewarmUntilPerfMsRef.current = now + 420;
            if (composedVideoElement) {
              try {
                composedVideoElement.currentTime = 0;
              } catch {
                // best effort
              }
              void composedVideoElement.play().catch(() => undefined);
            }
            onCursorChange(playbackStartMs);
            animationFrameId = window.requestAnimationFrame(tick);
            return;
          }
          cursorRef.current = playbackEndHoldCursorMs;
          forceVideoSyncRef.current = true;
          forceAudioSyncRef.current = true;
          playbackClockStartPerfMsRef.current = 0;
          playbackClockStartCursorMsRef.current = playbackEndHoldCursorMs;
          setIsPlaying(false);
          return;
        }
        animationFrameId = window.requestAnimationFrame(tick);
        return;
      }
      const basePerf = playbackClockStartPerfMsRef.current;
      const baseCursor = playbackClockStartCursorMsRef.current;
      const perfClockCursorMs = clamp(
        baseCursor + (basePerf > 0 ? Math.max(0, now - basePerf) : deltaMs) * timelinePlaybackRate,
        0,
        durationMs
      );
      const hasActiveCanvasVideo = hasActiveCanvasVideoAtCursor(cursorNowMs);
      const mediaClockHint = playbackMediaClockHintRef?.current ?? null;
      const hasFreshCanvasMediaClockHint =
        now >= mediaClockHoldUntilPerfMsRef.current &&
        Boolean(mediaClockHint) &&
        now - (mediaClockHint?.updatedAtMs ?? 0) <= 250;
      if (hasActiveCanvasVideo) {
        const hintedCursorMs =
          hasFreshCanvasMediaClockHint && mediaClockHint?.cursorMs !== null
            ? clamp(Number(mediaClockHint?.cursorMs), 0, durationMs)
            : null;
        const canUseHintedCursor =
          hintedCursorMs !== null &&
          Number.isFinite(hintedCursorMs) &&
          Math.abs(hintedCursorMs - cursorNowMs) <= Math.max(500, 1000 * Math.max(1, timelinePlaybackRate));
        if (canUseHintedCursor) {
          nextCursorMs = mediaClockHint?.isWaitingForMedia
            ? cursorNowMs
            : Math.max(cursorNowMs, hintedCursorMs ?? cursorNowMs);
        } else {
          nextCursorMs = perfClockCursorMs;
        }
        playbackClockStartPerfMsRef.current = now;
        playbackClockStartCursorMsRef.current = nextCursorMs;
      } else {
        // Kernel/wall clock is the master timeline authority: the cursor free-runs from
        // the perf clock and is committed to the kernel every CURSOR_COMMIT_INTERVAL_MS,
        // while video/audio elements seek to follow it in syncTimelineVideoElementsAtCursor.
        // Previously the cursor was slaved to the top active <video>'s currentTime and
        // froze (nextCursorMs = cursorNowMs) whenever that element was buffering or
        // seeking — which stalled the entire timeline in multi-layer playback. The
        // cursor must never wait on media readiness; media chases the cursor, not the
        // reverse.
        nextCursorMs = perfClockCursorMs;
        playbackClockStartPerfMsRef.current = now;
        playbackClockStartCursorMsRef.current = nextCursorMs;
      }
      cursorRef.current = nextCursorMs;
      syncTimelineVideoElementsAtCursor(nextCursorMs, true, now);
      const shouldHoldLastFrame = !isLoopEnabled && nextCursorMs >= playbackEndMs;
      const cursorToCommit = shouldHoldLastFrame ? playbackEndHoldCursorMs : nextCursorMs;
      const shouldCommit =
        now - lastCursorCommitTsRef.current >= CURSOR_COMMIT_INTERVAL_MS ||
        cursorToCommit <= 0 ||
        nextCursorMs >= playbackEndMs;
      if (shouldCommit) {
        lastCursorCommitTsRef.current = now;
        onCursorChange(cursorToCommit);
      }
      if (nextCursorMs >= playbackEndMs) {
        if (isLoopEnabled) {
          cursorRef.current = playbackStartMs;
          playbackClockStartPerfMsRef.current = now;
          playbackClockStartCursorMsRef.current = playbackStartMs;
          forceVideoSyncRef.current = true;
          forceAudioSyncRef.current = true;
          resetPlaybackClocksForDiscontinuity(playbackStartMs);
          mediaClockHoldUntilPerfMsRef.current = now + 420;
          suppressVideoPrewarmUntilPerfMsRef.current = now + 420;
          syncTimelineVideoElementsAtCursor(playbackStartMs, true, now);
          onCursorChange(playbackStartMs);
          animationFrameId = window.requestAnimationFrame(tick);
          return;
        }
        cursorRef.current = playbackEndHoldCursorMs;
        forceVideoSyncRef.current = true;
        forceAudioSyncRef.current = true;
        playbackClockStartPerfMsRef.current = 0;
        playbackClockStartCursorMsRef.current = playbackEndHoldCursorMs;
        syncTimelineVideoElementsAtCursor(playbackEndHoldCursorMs, false, now);
        setIsPlaying(false);
        return;
      }
      animationFrameId = window.requestAnimationFrame(tick);
    };

    animationFrameId = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [
    canPlay,
    composedPlaybackEnabled,
    composedPlaybackVideoRef,
    durationMs,
    isLoopEnabled,
    isMuted,
    kernel,
    onComposedPlaybackStateChange,
    onCursorChange,
    playbackEndHoldCursorMs,
    playbackEndMs,
    playbackStartMs,
    resolvedTracks,
    hasActiveCanvasVideoAtCursor,
    playbackMediaClockHintRef,
    syncTimelineVideoElementsAtCursor,
    timelineCanvasCompositorEnabled,
    timelinePlaybackRate,
  ]);

  const clearRulerDragListeners = useCallback(() => {
    const listeners = rulerDragListenersRef.current;
    if (!listeners) return;
    window.removeEventListener('mousemove', listeners.move);
    window.removeEventListener('mouseup', listeners.up);
    rulerDragListenersRef.current = null;
  }, []);

  const seekFromRulerClientX = useCallback((clientX: number) => {
    const rulerElement = rulerRef.current;
    if (!rulerElement) return;
    const rect = rulerElement.getBoundingClientRect();
    if (!Number.isFinite(rect.width) || rect.width <= 0) return;
    const relativeX = clamp(clientX - rect.left, 0, rect.width);
    const relativeMs = (relativeX / rect.width) * rulerRangeMs;
    const rawCursorMs = clamp(rulerRangeStartMs + relativeMs, rulerRangeStartMs, rulerRangeEndMs);
    const nextCursorMs = rawCursorMs >= playbackEndMs ? playbackEndHoldCursorMs : rawCursorMs;
    const targetTimeSec = Math.max(0, (nextCursorMs - playbackStartMs) / 1000);
    const composedVideoElement = composedPlaybackVideoRef?.current ?? null;
    if (
      composedPlaybackEnabled &&
      composedVideoElement &&
      composedVideoElement.readyState >= 1 &&
      !composedVideoElement.seeking
    ) {
      try {
        composedVideoElement.currentTime = targetTimeSec;
      } catch {
        // best effort seek; sync fallback remains enabled.
      }
      pausedComposedTimeSecRef.current = targetTimeSec;
    }
    pendingSeekCursorMsRef.current = isPlaying ? null : nextCursorMs;
    forceVideoSyncRef.current = true;
    forceAudioSyncRef.current = true;
    resetPlaybackClocksForDiscontinuity(nextCursorMs);
    if (isPlaying) {
      playbackClockStartPerfMsRef.current = performance.now();
      playbackClockStartCursorMsRef.current = nextCursorMs;
    }
    cursorRef.current = nextCursorMs;
    onCursorChange(nextCursorMs);
  }, [
    composedPlaybackEnabled,
    composedPlaybackVideoRef,
    isPlaying,
    resetPlaybackClocksForDiscontinuity,
    onCursorChange,
    playbackStartMs,
    playbackEndHoldCursorMs,
    playbackEndMs,
    rulerRangeEndMs,
    rulerRangeMs,
    rulerRangeStartMs,
  ]);

  const handleRulerMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    seekFromRulerClientX(event.clientX);
    clearRulerDragListeners();

    const handleMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      seekFromRulerClientX(moveEvent.clientX);
    };
    const handleUp = () => {
      clearRulerDragListeners();
    };
    rulerDragListenersRef.current = { move: handleMove, up: handleUp };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  }, [clearRulerDragListeners, seekFromRulerClientX]);

  useEffect(() => {
    return () => {
      clearRulerDragListeners();
    };
  }, [clearRulerDragListeners]);

  const seekPlaybackCursor = useCallback((nextCursorMs: number) => {
    forceVideoSyncRef.current = true;
    forceAudioSyncRef.current = true;
    resetPlaybackClocksForDiscontinuity(nextCursorMs);
    if (isPlaying) {
      playbackClockStartPerfMsRef.current = performance.now();
      playbackClockStartCursorMsRef.current = nextCursorMs;
    }
    cursorRef.current = nextCursorMs;
    onCursorChange(nextCursorMs);
  }, [isPlaying, resetPlaybackClocksForDiscontinuity, onCursorChange]);

  const pausePlaybackForEdit = useCallback(() => {
    pendingPlayAfterComposeReadyRef.current = false;
    setIsPlaying(false);
    forceVideoSyncRef.current = true;
    forceAudioSyncRef.current = true;
    playbackClockStartPerfMsRef.current = 0;
  }, []);

  const primeActiveAudioPlayback = useCallback((cursorMsOverride?: number) => {
    // Read the cursor from the ref, not from a dependency: clampedCursorMs is committed
    // every CURSOR_COMMIT_INTERVAL_MS during playback, so depending on it would rebuild
    // this callback ~20x/s and cascade through handlePrimaryAction into the effects that
    // register the global keydown listener.
    const primeAtCursorMs =
      typeof cursorMsOverride === 'number' && Number.isFinite(cursorMsOverride)
        ? cursorMsOverride
        : clampedCursorMsRef.current;
    const activeAudioTracks = resolvedTracks.filter((track) => {
      if (track.layerType !== 'audio') return false;
      return primeAtCursorMs >= track.startMs && primeAtCursorMs < track.endMs;
    });
    activeAudioTracks.forEach((track) => {
      const source = getTrackSource(track);
      if (source.length === 0) return;
      let audioElement = audioElementsRef.current[track.layerId];
      if (!audioElement) {
        audioElement = new Audio();
        audioElement.preload = 'auto';
        audioElement.crossOrigin = 'anonymous';
        audioElement.loop = false;
        audioElement.src = source;
        audioElementsRef.current[track.layerId] = audioElement;
      } else if (
        normalizeMediaSourceForCompare(audioElement.currentSrc || audioElement.src) !==
        normalizeMediaSourceForCompare(source)
      ) {
        audioElement.src = source;
      }
      const effectiveAudioGain = getNormalizedAudioGain(track.audioGain);
      setTimelineAudioOutputGain(track.layerId, audioElement, isMuted, effectiveAudioGain);
      audioElement.loop = false;
      audioElement.playbackRate = timelinePlaybackRate;
      const trackRelativeMs = clamp(
        primeAtCursorMs - track.startMs + track.sourceInMs,
        0,
        Number.MAX_SAFE_INTEGER
      );
      try {
        audioElement.currentTime = trackRelativeMs / 1000;
      } catch {
        // Metadata may still be loading; playback will start once ready.
      }
      void audioElement.play().catch(() => undefined);
    });
  }, [isMuted, resolvedTracks, setTimelineAudioOutputGain, timelinePlaybackRate]);

  const handlePrimaryAction = useCallback(() => {
    if (!hasAnyTrack) return;
    const run = async () => {
      const pendingSeekCursorMs = pendingSeekCursorMsRef.current;
      const composedVideoElement = composedPlaybackEnabled
        ? (composedPlaybackVideoRef?.current ?? null)
        : null;
      const composedLiveCursorMs =
        composedVideoElement && Number.isFinite(composedVideoElement.currentTime)
          ? clamp(playbackStartMs + composedVideoElement.currentTime * 1000, 0, durationMs)
          : null;
      let liveCursorMs = composedLiveCursorMs ?? clamp(cursorRef.current, 0, durationMs);
      if (isPlaying) {
        pendingPlayAfterComposeReadyRef.current = false;
        // Persist the exact in-flight cursor before switching to paused state.
        const pausedCursorMs = liveCursorMs;
        cursorRef.current = pausedCursorMs;
        onCursorChange(pausedCursorMs);
        if (composedPlaybackEnabled) {
          if (composedVideoElement) {
            pausedComposedTimeSecRef.current = Math.max(0, composedVideoElement.currentTime || 0);
            if (!composedVideoElement.paused) {
              composedVideoElement.pause();
            }
          }
        }
        forceVideoSyncRef.current = false;
        forceAudioSyncRef.current = false;
        playbackClockStartPerfMsRef.current = 0;
        playbackClockStartCursorMsRef.current = pausedCursorMs;
        setIsPlaying(false);
        return;
      }

      const endRestartThresholdMs = Math.max(
        playbackStartMs,
        playbackEndHoldCursorMs - TIMELINE_END_RESTART_MARGIN_MS
      );
      const isPausedAtTimelineEnd = pendingSeekCursorMs === null && (
        liveCursorMs >= endRestartThresholdMs ||
        clampedCursorMsRef.current >= endRestartThresholdMs
      );
      if (isPausedAtTimelineEnd) {
        liveCursorMs = playbackStartMs;
        cursorRef.current = playbackStartMs;
        resetPlaybackClocksForDiscontinuity(playbackStartMs);
        onCursorChange(playbackStartMs);
        syncTimelineVideoElementsAtCursor(playbackStartMs, false);
        mediaClockHoldUntilPerfMsRef.current = performance.now() + 420;
        suppressVideoPrewarmUntilPerfMsRef.current = performance.now() + 420;
      }

      if (hasPlayableMediaTrack && !isFastPlaybackPrepared) {
        pendingPlayAfterComposeReadyRef.current = true;
        if (isPreparingComposedPlayback) {
          return;
        }
        await prepareFastPlaybackReady();
        return;
      }
      pendingPlayAfterComposeReadyRef.current = false;

      let targetCursorMs = pendingSeekCursorMs !== null
        ? clamp(pendingSeekCursorMs, 0, durationMs)
        : liveCursorMs;
      let shouldForceSync = pendingSeekCursorMs !== null || isPausedAtTimelineEnd;
      if (liveCursorMs >= Math.max(playbackStartMs, playbackEndHoldCursorMs - 1) && !isPlaying) {
        shouldForceSync = true;
        cursorRef.current = playbackStartMs;
        resetPlaybackClocksForDiscontinuity(playbackStartMs);
        onCursorChange(playbackStartMs);
        targetCursorMs = playbackStartMs;
        mediaClockHoldUntilPerfMsRef.current = performance.now() + 420;
        suppressVideoPrewarmUntilPerfMsRef.current = performance.now() + 420;
        if (!hasPlayableMediaTrack) {
          primeActiveAudioPlayback(playbackStartMs);
        }
      } else {
        if (liveCursorMs < playbackStartMs) {
          shouldForceSync = true;
          cursorRef.current = playbackStartMs;
          resetPlaybackClocksForDiscontinuity(playbackStartMs);
          onCursorChange(playbackStartMs);
          targetCursorMs = playbackStartMs;
          mediaClockHoldUntilPerfMsRef.current = performance.now() + 420;
          suppressVideoPrewarmUntilPerfMsRef.current = performance.now() + 420;
          if (!hasPlayableMediaTrack) {
            primeActiveAudioPlayback(playbackStartMs);
          }
        } else {
          targetCursorMs = liveCursorMs;
          if (!hasPlayableMediaTrack) {
            primeActiveAudioPlayback(liveCursorMs);
          }
        }
      }
      forceVideoSyncRef.current = shouldForceSync;
      forceAudioSyncRef.current = shouldForceSync;
      pendingSeekCursorMsRef.current = null;
      playbackClockStartPerfMsRef.current = performance.now();
      playbackClockStartCursorMsRef.current = targetCursorMs;

      if (composedPlaybackEnabled) {
        const targetTimeSec = Math.max(0, (targetCursorMs - playbackStartMs) / 1000);
        if (composedVideoElement) {
          if (shouldForceSync) {
            if (composedVideoElement.readyState >= 1) {
              try {
                composedVideoElement.currentTime = targetTimeSec;
              } catch {
                // Best effort; follow-up sync path will retry.
              }
            }
          }
          composedVideoElement.loop = false;
          composedVideoElement.playbackRate = timelinePlaybackRate;
          composedVideoElement.muted = isMuted;
          composedVideoElement.volume = isMuted ? 0 : 1;
          pausedComposedTimeSecRef.current = null;
          void composedVideoElement.play().catch(() => {
            setComposedMediaSyncTick((current) => current + 1);
          });
        }
      }
      setIsPlaying(true);
    };
    void run();
    // clampedCursorMs is read through clampedCursorMsRef so this handler stays stable
    // during playback: it is returned to consumers and drives the global keydown effect.
  }, [
    composedPlaybackEnabled,
    composedPlaybackVideoRef,
    hasPlayableMediaTrack,
    isFastPlaybackPrepared,
    isPreparingComposedPlayback,
    hasAnyTrack,
    isMuted,
    isPlaying,
    resetPlaybackClocksForDiscontinuity,
    onCursorChange,
    durationMs,
    playbackEndHoldCursorMs,
    playbackStartMs,
    prepareFastPlaybackReady,
    primeActiveAudioPlayback,
    syncTimelineVideoElementsAtCursor,
    timelinePlaybackRate,
  ]);

  const handleToggleLoop = useCallback(() => {
    const k = kernel.current;
    if (!k) return;
    setWorkspaceIsTimelineLoopEnabled(k, workspaceId, (current) => !current);
  }, [kernel, workspaceId]);

  const handleStepBackward = useCallback(() => {
    const next = Math.max(0, (cursorRef.current ?? 0) - endHoldFrameDurationMs);
    forceVideoSyncRef.current = true;
    forceAudioSyncRef.current = true;
    resetPlaybackClocksForDiscontinuity(next);
    if (isPlaying) {
      playbackClockStartPerfMsRef.current = performance.now();
      playbackClockStartCursorMsRef.current = next;
    }
    cursorRef.current = next;
    onCursorChange(next);
  }, [isPlaying, resetPlaybackClocksForDiscontinuity, onCursorChange, endHoldFrameDurationMs]);

  const handleStepForward = useCallback(() => {
    const rawNext = Math.min(durationMs, (cursorRef.current ?? 0) + endHoldFrameDurationMs);
    const next = rawNext >= playbackEndMs ? playbackEndHoldCursorMs : rawNext;
    forceVideoSyncRef.current = true;
    forceAudioSyncRef.current = true;
    resetPlaybackClocksForDiscontinuity(next);
    if (isPlaying) {
      playbackClockStartPerfMsRef.current = performance.now();
      playbackClockStartCursorMsRef.current = next;
    }
    cursorRef.current = next;
    onCursorChange(next);
  }, [durationMs, isPlaying, resetPlaybackClocksForDiscontinuity, onCursorChange, playbackEndHoldCursorMs, playbackEndMs, endHoldFrameDurationMs]);

  const updateTimelinePlaybackRate = useCallback((rate: number) => {
    const parsedRate = toFinite(rate);
    if (parsedRate === null) return;
    const nextRate = clamp(parsedRate, MIN_TIMELINE_PLAYBACK_RATE, MAX_TIMELINE_PLAYBACK_RATE);
    const k = kernel.current;
    if (!k) return;
    const currentRate = getWorkspaceTimelinePlaybackRate(k, workspaceId);
    if (Math.abs(nextRate - currentRate) < TIMELINE_PLAYBACK_RATE_EPSILON) {
      return;
    }
    const rebasedCursorMs = clamp(cursorRef.current, 0, durationMs);
    cursorRef.current = rebasedCursorMs;
    playbackClockStartPerfMsRef.current = performance.now();
    playbackClockStartCursorMsRef.current = rebasedCursorMs;
    forceVideoSyncRef.current = true;
    forceAudioSyncRef.current = true;
    setWorkspaceTimelinePlaybackRate(k, workspaceId, nextRate);
  }, [durationMs, kernel, workspaceId]);

  const handleSelectPlaybackRate = useCallback((rate: number) => {
    updateTimelinePlaybackRate(rate);
  }, [updateTimelinePlaybackRate]);

  const handleToggleMuted = useCallback(() => {
    const k = kernel.current;
    if (!k) return;
    const nextMuted = !getWorkspaceIsTimelineMuted(k, workspaceId);
    if (!nextMuted && isPlaying && !hasPlayableMediaTrack) {
      primeActiveAudioPlayback(cursorRef.current);
    }
    setWorkspaceIsTimelineMuted(k, workspaceId, nextMuted);
  }, [hasPlayableMediaTrack, isPlaying, primeActiveAudioPlayback, kernel, workspaceId]);

  const handleToggleVersionView = useCallback(() => {
    pendingPlayAfterComposeReadyRef.current = false;
    if (isPlaying) {
      setIsPlaying(false);
    }
    playbackClockStartPerfMsRef.current = 0;
    onToggleVersionView();
  }, [isPlaying, onToggleVersionView]);

  useEffect(() => {
    if (!pendingPlayAfterComposeReadyRef.current) return;
    if (!isActive || showVersionView || isPlaying) return;
    if (hasPlayableMediaTrack && !isFastPlaybackPrepared) return;
    pendingPlayAfterComposeReadyRef.current = false;
    handlePrimaryAction();
  }, [
    handlePrimaryAction,
    hasPlayableMediaTrack,
    isActive,
    isFastPlaybackPrepared,
    isPlaying,
    showVersionView,
  ]);

  useEffect(() => {
    if (isActive && !showVersionView) return;
    pendingPlayAfterComposeReadyRef.current = false;
  }, [isActive, showVersionView]);

  useEffect(() => {
    const composedVideoElement = composedPlaybackVideoRef?.current ?? null;
    if (!composedVideoElement) {
      if (composedPlaybackEnabled && canPlay) {
        if (composedPlayRetryTimerRef.current !== null) {
          window.clearTimeout(composedPlayRetryTimerRef.current);
        }
        composedPlayRetryTimerRef.current = window.setTimeout(() => {
          composedPlayRetryTimerRef.current = null;
          setComposedMediaSyncTick((current) => current + 1);
        }, 80);
      }
      return;
    }
    if (!composedPlaybackEnabled) {
      if (composedPlayRetryTimerRef.current !== null) {
        window.clearTimeout(composedPlayRetryTimerRef.current);
        composedPlayRetryTimerRef.current = null;
      }
      if (!composedVideoElement.paused) {
        composedVideoElement.pause();
      }
      return;
    }

    composedVideoElement.loop = false;
    composedVideoElement.playbackRate = timelinePlaybackRate;
    composedVideoElement.muted = isMuted || !canPlay;
    composedVideoElement.volume = isMuted || !canPlay ? 0 : 1;

    const syncCursorMs = clamp(cursorRef.current, 0, durationMs);
    const targetTimeSec = Math.max(0, (syncCursorMs - playbackStartMs) / 1000);
    const shouldHardSync = forceVideoSyncRef.current;
    if (shouldHardSync) {
      try {
        if (composedVideoElement.readyState >= 1 && !composedVideoElement.seeking) {
          composedVideoElement.currentTime = targetTimeSec;
        }
      } catch {
        // best effort
      }
      if (!composedVideoElement.seeking) {
        forceVideoSyncRef.current = false;
      }
    } else if (!canPlay) {
      forceVideoSyncRef.current = false;
    }

    if (canPlay) {
      if (composedPlayRetryTimerRef.current !== null) {
        window.clearTimeout(composedPlayRetryTimerRef.current);
        composedPlayRetryTimerRef.current = null;
      }
      pausedComposedTimeSecRef.current = null;
      if (!composedVideoElement.seeking && composedVideoElement.paused) {
        const shouldBootstrapMutedAutoplay = !isMuted;
        if (shouldBootstrapMutedAutoplay) {
          composedVideoElement.muted = true;
          composedVideoElement.volume = 0;
        }
        void composedVideoElement.play().then(() => {
          if (!isPlaying) return;
          if (shouldBootstrapMutedAutoplay && !isMuted) {
            composedVideoElement.muted = false;
            composedVideoElement.volume = 1;
          }
        }).catch(() => {
          if (!isPlaying) return;
          if (composedPlayRetryTimerRef.current !== null) {
            window.clearTimeout(composedPlayRetryTimerRef.current);
          }
          composedPlayRetryTimerRef.current = window.setTimeout(() => {
            composedPlayRetryTimerRef.current = null;
            setComposedMediaSyncTick((current) => current + 1);
          }, 140);
        });
      }
    } else {
      const pausedTimeSec = pausedComposedTimeSecRef.current;
      if (
        pausedTimeSec !== null &&
        Number.isFinite(pausedTimeSec) &&
        composedVideoElement.readyState >= 1 &&
        !composedVideoElement.seeking &&
        Math.abs(composedVideoElement.currentTime - pausedTimeSec) > 0.05
      ) {
        try {
          composedVideoElement.currentTime = pausedTimeSec;
        } catch {
          // best effort
        }
      }
      if (!composedVideoElement.paused) {
        composedVideoElement.pause();
      }
      if (composedPlayRetryTimerRef.current !== null) {
        window.clearTimeout(composedPlayRetryTimerRef.current);
        composedPlayRetryTimerRef.current = null;
      }
    }
  }, [
    canPlay,
    clampedCursorMs,
    composedMediaSyncTick,
    composedPlaybackEnabled,
    composedPlaybackVideoRef,
    isMuted,
    isLoopEnabled,
    isPlaying,
    durationMs,
    playbackStartMs,
    timelinePlaybackRate,
  ]);

  useEffect(() => {
    const previewCursorMs = isPlaying
      ? clamp(cursorRef.current, 0, durationMs)
      : clampedCursorMs;
    onComposedPlaybackStateChange?.({
      enabled: timelineCanvasCompositorEnabled,
      sourceUrl: composedPlaybackEnabled ? composedPlaybackSourceUrl : null,
      isPlaying,
      isMuted,
      isPreparing: isComposedPreparing,
      preparePercent: composedPreparePercent,
      prepareMessage: composedPrepareMessage,
      tracks: previewResolvedTracks,
      cursorMs: previewCursorMs,
      playbackRate: timelinePlaybackRate,
      playbackStartMs,
      playbackEndMs,
    });
  }, [
    clampedCursorMs,
    composedPlaybackEnabled,
    composedPlaybackSourceUrl,
    composedPrepareMessage,
    composedPreparePercent,
    durationMs,
    isComposedPreparing,
    isMuted,
    isPlaying,
    onComposedPlaybackStateChange,
    playbackEndMs,
    playbackStartMs,
    previewResolvedTracks,
    timelineCanvasCompositorEnabled,
    timelinePlaybackRate,
  ]);

  useEffect(() => {
    return () => {
      onComposedPlaybackStateChange?.({
        enabled: false,
        sourceUrl: null,
        isPlaying: false,
        isMuted: true,
        isPreparing: false,
        preparePercent: 0,
        prepareMessage: null,
        tracks: [],
        cursorMs: 0,
        playbackRate: DEFAULT_TIMELINE_PLAYBACK_RATE,
        playbackStartMs: 0,
        playbackEndMs: 0,
      });
    };
  }, [onComposedPlaybackStateChange]);

  useEffect(() => {
    if (!isActive || showVersionView || !hasAnyTrack || !hasPlayableMediaTrack) return;

    const isTypingTarget = (target: EventTarget | null): boolean => {
      const element = target as HTMLElement | null;
      if (!element) return false;
      if (element.isContentEditable) return true;
      const tagName = element.tagName?.toLowerCase();
      return tagName === 'input' || tagName === 'textarea' || tagName === 'select' || tagName === 'button';
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;

      const isSpace = event.code === 'Space' || event.key === ' ';
      if (isSpace) {
        if (event.repeat) return;
        event.preventDefault();
        handlePrimaryAction();
        return;
      }

      // In timeline display mode the left/right arrows step the playback cursor
      // one frame instead of cycling workspace versions (the kernel's
      // handleCanvasKeyDown, bubble phase on the canvas container). This
      // capture-phase listener runs before that handler; stopPropagation keeps
      // the version-navigation path from also firing. Repeat is allowed so
      // holding an arrow scrubs frame by frame.
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        event.stopPropagation();
        if (event.key === 'ArrowLeft') {
          handleStepBackward();
        } else {
          handleStepForward();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [
    hasAnyTrack,
    hasPlayableMediaTrack,
    handlePrimaryAction,
    handleStepBackward,
    handleStepForward,
    isActive,
    showVersionView,
  ]);

  const primaryControlLabel = useMemo(() => {
    if (isComposedPreparing) {
      const percent = Math.max(0, Math.min(100, Math.round(composedPreparePercent)));
      return percent > 0
        ? `Buffering timeline media (${percent}%)`
        : 'Buffering timeline media...';
    }
    if (isAtTimelineEnd && !isPlaying) return 'Restart timeline';
    if (isPlaying) return 'Pause timeline';
    return 'Play timeline';
  }, [composedPreparePercent, isAtTimelineEnd, isComposedPreparing, isPlaying]);
  const playbackRateLabel = useMemo(
    () => formatTimelinePlaybackRate(timelinePlaybackRate),
    [timelinePlaybackRate]
  );

  return useMemo(() => ({
    isPlaying,
    isLoopEnabled,
    isMuted,
    timelinePlaybackRate,
    isAtTimelineEnd,
    isComposedPreparing,
    primaryControlLabel,
    playbackRateLabel,
    clampedCursorMsRef,
    rulerRef,
    handlePrimaryAction,
    handleStepBackward,
    handleStepForward,
    handleToggleLoop,
    handleToggleMuted,
    handleSelectPlaybackRate,
    handleToggleVersionView,
    handleRulerMouseDown,
    seekPlaybackCursor,
    pausePlaybackForEdit,
  }), [
    handlePrimaryAction,
    handleRulerMouseDown,
    handleSelectPlaybackRate,
    handleStepBackward,
    handleStepForward,
    handleToggleLoop,
    handleToggleMuted,
    handleToggleVersionView,
    isAtTimelineEnd,
    isComposedPreparing,
    isLoopEnabled,
    isMuted,
    isPlaying,
    pausePlaybackForEdit,
    playbackRateLabel,
    primaryControlLabel,
    seekPlaybackCursor,
    timelinePlaybackRate,
  ]);
};
